import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Icon from '../components/ui/Icon';

export type NotificationType = 'success' | 'error' | 'info' | 'warning' | 'confirm' | 'prompt';

export interface AlertOptions {
  title?: string;
  type?: Exclude<NotificationType, 'confirm' | 'prompt'>;
}

export interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

export interface PromptOptions {
  title?: string;
  label?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface NotificationModalState {
  isOpen: boolean;
  mode: NotificationType;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  destructive: boolean;
  promptLabel: string;
  promptValue: string;
  promptPlaceholder: string;
}

interface NotificationModalContextValue {
  alert: (message: string, options?: AlertOptions) => Promise<void>;
  confirm: (message: string, options?: ConfirmOptions) => Promise<boolean>;
  prompt: (message: string, options?: PromptOptions) => Promise<string | null>;
}

const DEFAULT_STATE: NotificationModalState = {
  isOpen: false,
  mode: 'info',
  title: '',
  message: '',
  confirmLabel: 'OK',
  cancelLabel: 'Cancel',
  destructive: false,
  promptLabel: '',
  promptValue: '',
  promptPlaceholder: '',
};

const TITLE_BY_TYPE: Record<Exclude<NotificationType, 'confirm' | 'prompt'>, string> = {
  success: 'Success',
  error: 'Error',
  info: 'Notice',
  warning: 'Warning',
};

const NotificationModalContext = createContext<NotificationModalContextValue | null>(null);

type Resolver =
  | { kind: 'alert'; resolve: () => void }
  | { kind: 'confirm'; resolve: (value: boolean) => void }
  | { kind: 'prompt'; resolve: (value: string | null) => void };

let imperativeApi: NotificationModalContextValue | null = null;

export const appAlert = (message: string, options?: AlertOptions) =>
  imperativeApi?.alert(message, options) ?? Promise.resolve();

export const appConfirm = (message: string, options?: ConfirmOptions) =>
  imperativeApi?.confirm(message, options) ?? Promise.resolve(false);

export const appPrompt = (message: string, options?: PromptOptions) =>
  imperativeApi?.prompt(message, options) ?? Promise.resolve(null);

export const useNotificationModal = (): NotificationModalContextValue => {
  const context = useContext(NotificationModalContext);
  if (!context) {
    throw new Error('useNotificationModal must be used within NotificationModalProvider');
  }
  return context;
};

export const NotificationModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modal, setModal] = useState<NotificationModalState>(DEFAULT_STATE);
  const resolverRef = useRef<Resolver | null>(null);
  const promptInputRef = useRef<HTMLInputElement>(null);

  const finish = useCallback((nextState: Partial<NotificationModalState> = {}) => {
    setModal({ ...DEFAULT_STATE, ...nextState, isOpen: false });
    resolverRef.current = null;
  }, []);

  const alert = useCallback((message: string, options?: AlertOptions) => {
    const type = options?.type ?? 'info';
    return new Promise<void>((resolve) => {
      resolverRef.current = { kind: 'alert', resolve };
      setModal({
        ...DEFAULT_STATE,
        isOpen: true,
        mode: type,
        title: options?.title ?? TITLE_BY_TYPE[type],
        message,
        confirmLabel: 'OK',
      });
    });
  }, []);

  const confirm = useCallback((message: string, options?: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = { kind: 'confirm', resolve };
      setModal({
        ...DEFAULT_STATE,
        isOpen: true,
        mode: 'confirm',
        title: options?.title ?? 'Confirm',
        message,
        confirmLabel: options?.confirmLabel ?? 'Confirm',
        cancelLabel: options?.cancelLabel ?? 'Cancel',
        destructive: options?.destructive ?? true,
      });
    });
  }, []);

  const prompt = useCallback((message: string, options?: PromptOptions) => {
    return new Promise<string | null>((resolve) => {
      resolverRef.current = { kind: 'prompt', resolve };
      setModal({
        ...DEFAULT_STATE,
        isOpen: true,
        mode: 'prompt',
        title: options?.title ?? 'Input Required',
        message,
        promptLabel: options?.label ?? '',
        promptValue: options?.defaultValue ?? '',
        promptPlaceholder: options?.placeholder ?? '',
        confirmLabel: options?.confirmLabel ?? 'OK',
        cancelLabel: options?.cancelLabel ?? 'Cancel',
      });
    });
  }, []);

  const value = useMemo(
    () => ({ alert, confirm, prompt }),
    [alert, confirm, prompt]
  );

  useEffect(() => {
    imperativeApi = value;
    return () => {
      if (imperativeApi === value) {
        imperativeApi = null;
      }
    };
  }, [value]);

  useEffect(() => {
    if (modal.isOpen && modal.mode === 'prompt') {
      promptInputRef.current?.focus();
      promptInputRef.current?.select();
    }
  }, [modal.isOpen, modal.mode]);

  const handleClose = () => {
    const resolver = resolverRef.current;
    if (!resolver) {
      finish();
      return;
    }
    if (resolver.kind === 'alert') {
      resolver.resolve();
    } else if (resolver.kind === 'confirm') {
      resolver.resolve(false);
    } else {
      resolver.resolve(null);
    }
    finish();
  };

  const handleConfirm = () => {
    const resolver = resolverRef.current;
    if (!resolver) {
      finish();
      return;
    }
    if (resolver.kind === 'alert') {
      resolver.resolve();
    } else if (resolver.kind === 'confirm') {
      resolver.resolve(true);
    } else {
      resolver.resolve(modal.promptValue);
    }
    finish();
  };

  const handleOverlayClick = () => {
    if (modal.mode === 'confirm' || modal.mode === 'prompt') {
      handleClose();
      return;
    }
    handleConfirm();
  };

  const iconName =
    modal.mode === 'success'
      ? 'check-circle'
      : modal.mode === 'error'
        ? 'x-circle'
        : modal.mode === 'warning' || modal.mode === 'confirm'
          ? 'warning'
          : modal.mode === 'prompt'
            ? 'edit'
            : 'info';

  return (
    <NotificationModalContext.Provider value={value}>
      {children}
      {modal.isOpen && (
        <div className="modal-overlay app-notification-modal" onClick={handleOverlayClick}>
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3 className={`modal-title ${modal.mode === 'prompt' ? 'info' : modal.mode}`}>
                <Icon name={iconName} />
                {modal.title}
              </h3>
              <button className="modal-close" onClick={handleClose} aria-label="Close" type="button">
                <Icon name="close" />
              </button>
            </div>
            <div className="modal-body">
              <p>{modal.message}</p>
              {modal.mode === 'prompt' && (
                <div className="app-notification-modal__prompt">
                  {modal.promptLabel ? <label htmlFor="app-notification-prompt">{modal.promptLabel}</label> : null}
                  <input
                    ref={promptInputRef}
                    id="app-notification-prompt"
                    type="text"
                    value={modal.promptValue}
                    placeholder={modal.promptPlaceholder}
                    onChange={(event) =>
                      setModal((current) => ({ ...current, promptValue: event.target.value }))
                    }
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleConfirm();
                      }
                    }}
                  />
                </div>
              )}
            </div>
            <div className="modal-footer">
              {modal.mode === 'confirm' || modal.mode === 'prompt' ? (
                <>
                  <button className="btn btn-secondary" onClick={handleClose} type="button">
                    {modal.cancelLabel}
                  </button>
                  <button
                    className={`btn ${modal.destructive ? 'btn-danger' : 'btn-primary'}`}
                    onClick={handleConfirm}
                    type="button"
                  >
                    {modal.confirmLabel}
                  </button>
                </>
              ) : (
                <button className="btn btn-primary" onClick={handleConfirm} type="button">
                  {modal.confirmLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </NotificationModalContext.Provider>
  );
};
